class HbnStoryGlimpse extends HTMLElement {
  connectedCallback() {
    if (this.dataset.hbnReady === 'true') return;

    this.dataset.hbnReady = 'true';
    this.spreadCount = Number.parseInt(this.dataset.spreadCount || '0', 10);
    this.coverMode = this.dataset.coverMode === 'true';
    this.currentSpread = 0;
    this.isAnimating = false;
    this.preloadedImages = [];
    this.bookOuterRadius = 'clamp(5px, 0.5vw, 8px)';
    this.bookSurfaceShadow =
      '0 16px 36px rgba(61, 34, 78, 0.22), 0 4px 12px rgba(61, 34, 78, 0.14)';

    this.book = this.querySelector('[data-hbn-book]');
    this.leftPage = this.querySelector('[data-hbn-left-page]');
    this.rightPage = this.querySelector('[data-hbn-right-page]');
    this.leaf = this.querySelector('[data-hbn-leaf]');
    this.leafFront = this.querySelector('[data-hbn-leaf-front]');
    this.leafBack = this.querySelector('[data-hbn-leaf-back]');
    this.previousButton = this.querySelector('[data-hbn-previous]');
    this.nextButton = this.querySelector('[data-hbn-next]');
    this.currentCounter = this.querySelector('[data-hbn-current]');
    this.status = this.querySelector('[data-hbn-status]');
    this.viewer = this.querySelector('.hbn-story-glimpse__viewer');
    this.bookExpandButton = this.querySelector('[data-hbn-book-open]');
    this.bookDialog = this.querySelector('[data-hbn-book-dialog]');
    this.bookDialogStage = this.querySelector('[data-hbn-book-stage]');
    this.bookDialogClose = this.querySelector('[data-hbn-book-close]');
    this.sourceTriggers = Array.from(this.querySelectorAll('[data-hbn-source-open]'));
    this.sourceDialog = this.querySelector('[data-hbn-source-dialog]');
    this.sourceDialogImage = this.querySelector('[data-hbn-source-image]');
    this.sourceDialogCaption = this.querySelector('[data-hbn-source-caption]');
    this.sourceDialogClose = this.querySelector('[data-hbn-source-close]');
    this.pageTemplates = Array.from(this.querySelectorAll('template[data-hbn-page-template]'));

    if (
      !this.book ||
      !this.leftPage ||
      !this.rightPage ||
      !this.leaf ||
      !this.viewer ||
      this.spreadCount < 1 ||
      this.pageTemplates.length < this.spreadCount * 2
    ) {
      return;
    }

    this.applyBookGeometry();

    this.onPreviousClick = () => this.goTo(this.currentSpread - 1);
    this.onNextClick = () => this.goTo(this.currentSpread + 1);
    this.onBookKeydown = (event) => this.handleBookKeydown(event);
    this.onPointerDown = (event) => this.handlePointerDown(event);
    this.onPointerUp = (event) => this.handlePointerUp(event);
    this.onPointerCancel = () => this.clearPointerStart();
    this.onSourceOpen = (event) => this.openSourceImage(event.currentTarget);
    this.onSourceClose = () => this.closeSourceImage();
    this.onSourceDialogClick = (event) => {
      if (event.target === this.sourceDialog) this.closeSourceImage();
    };
    this.onSourceDialogClosed = () => this.resetSourceDialog();
    this.onBookExpand = () => this.openBookPreview();
    this.onBookClose = () => this.closeBookPreview();
    this.onBookDialogClick = (event) => {
      if (!event.target?.closest?.('.hbn-story-glimpse__viewer') &&
          !event.target?.closest?.('[data-hbn-book-close]')) {
        this.closeBookPreview();
      }
    };
    this.onBookDialogClosed = () => this.restoreBookViewer();
    this.onProtectedImageAction = (event) => {
      if (event.target?.closest?.('img')) event.preventDefault();
    };

    this.previousButton?.addEventListener('click', this.onPreviousClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.book.addEventListener('keydown', this.onBookKeydown);
    this.book.addEventListener('pointerdown', this.onPointerDown, { passive: true });
    this.book.addEventListener('pointerup', this.onPointerUp, { passive: true });
    this.book.addEventListener('pointercancel', this.onPointerCancel, { passive: true });
    this.sourceTriggers.forEach((trigger) => trigger.addEventListener('click', this.onSourceOpen));
    this.sourceDialogClose?.addEventListener('click', this.onSourceClose);
    this.sourceDialog?.addEventListener('click', this.onSourceDialogClick);
    this.sourceDialog?.addEventListener('close', this.onSourceDialogClosed);
    this.bookExpandButton?.addEventListener('click', this.onBookExpand);
    this.bookDialogClose?.addEventListener('click', this.onBookClose);
    this.bookDialog?.addEventListener('click', this.onBookDialogClick);
    this.bookDialog?.addEventListener('close', this.onBookDialogClosed);
    this.addEventListener('contextmenu', this.onProtectedImageAction);
    this.addEventListener('dragstart', this.onProtectedImageAction);

    this.renderSpread(0);
    this.updateControls();
    this.preparePreloading();
  }

  disconnectedCallback() {
    this.previousButton?.removeEventListener('click', this.onPreviousClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.book?.removeEventListener('keydown', this.onBookKeydown);
    this.book?.removeEventListener('pointerdown', this.onPointerDown);
    this.book?.removeEventListener('pointerup', this.onPointerUp);
    this.book?.removeEventListener('pointercancel', this.onPointerCancel);
    this.sourceTriggers?.forEach((trigger) => trigger.removeEventListener('click', this.onSourceOpen));
    this.sourceDialogClose?.removeEventListener('click', this.onSourceClose);
    this.sourceDialog?.removeEventListener('click', this.onSourceDialogClick);
    this.sourceDialog?.removeEventListener('close', this.onSourceDialogClosed);
    this.bookExpandButton?.removeEventListener('click', this.onBookExpand);
    this.bookDialogClose?.removeEventListener('click', this.onBookClose);
    this.bookDialog?.removeEventListener('click', this.onBookDialogClick);
    this.bookDialog?.removeEventListener('close', this.onBookDialogClosed);
    this.removeEventListener('contextmenu', this.onProtectedImageAction);
    this.removeEventListener('dragstart', this.onProtectedImageAction);
    this.preloadObserver?.disconnect();
    window.clearTimeout(this.transitionFallback);
  }

  applyBookGeometry() {
    const leftRadius = `${this.bookOuterRadius} 0 0 ${this.bookOuterRadius}`;
    const rightRadius = `0 ${this.bookOuterRadius} ${this.bookOuterRadius} 0`;
    const paper = this.book?.querySelector('.hbn-story-glimpse__paper');

    this.style.setProperty('--hbn-glimpse-outer-radius', this.bookOuterRadius);
    this.style.setProperty('--hbn-glimpse-book-shadow', this.bookSurfaceShadow);

    this.viewer?.style.setProperty('overflow', 'visible', 'important');
    this.book?.style.setProperty('overflow', 'visible', 'important');
    this.book?.style.setProperty('background', 'transparent', 'important');
    this.book?.style.setProperty('border-radius', '0', 'important');
    this.book?.style.setProperty('box-shadow', 'none', 'important');
    paper?.style.setProperty('overflow', 'visible', 'important');
    paper?.style.setProperty('background', 'transparent', 'important');

    this.leftPage?.style.setProperty('overflow', 'hidden', 'important');
    this.leftPage?.style.setProperty('background', 'transparent', 'important');
    this.leftPage?.style.setProperty('border-radius', leftRadius, 'important');
    this.leftPage?.style.setProperty('box-shadow', 'none', 'important');
    this.rightPage?.style.setProperty('overflow', 'hidden', 'important');
    this.rightPage?.style.setProperty('background', 'transparent', 'important');
    this.rightPage?.style.setProperty('border-radius', rightRadius, 'important');
    this.rightPage?.style.setProperty('box-shadow', 'none', 'important');

    this.leaf?.style.setProperty('background', 'transparent', 'important');
    this.leaf?.style.setProperty('border-radius', '0', 'important');
    this.leafFront?.style.setProperty('overflow', 'hidden', 'important');
    this.leafFront?.style.setProperty('background', 'transparent', 'important');
    this.leafBack?.style.setProperty('overflow', 'hidden', 'important');
    this.leafBack?.style.setProperty('background', 'transparent', 'important');
  }

  applyLeafGeometry(movingForward) {
    const leftRadius = `${this.bookOuterRadius} 0 0 ${this.bookOuterRadius}`;
    const rightRadius = `0 ${this.bookOuterRadius} ${this.bookOuterRadius} 0`;

    this.leafFront?.style.setProperty(
      'border-radius',
      movingForward ? rightRadius : leftRadius,
      'important'
    );
    this.leafBack?.style.setProperty(
      'border-radius',
      movingForward ? leftRadius : rightRadius,
      'important'
    );
  }

  applyBookChrome(position = this.book?.dataset.hbnPosition) {
    this.book?.style.setProperty('border-radius', '0', 'important');
    this.book?.style.setProperty('box-shadow', 'none', 'important');
    this.leftPage?.style.setProperty('box-shadow', 'none', 'important');
    this.rightPage?.style.setProperty('box-shadow', 'none', 'important');
    this.leafFront?.style.setProperty('box-shadow', 'none', 'important');
    this.leafBack?.style.setProperty('box-shadow', 'none', 'important');

    if (position === 'turning-cover') {
      // Only the visible paper casts a shadow; the empty half stays completely clear.
      [this.leftPage, this.rightPage, this.leafFront, this.leafBack].forEach((page) => {
        if (page && !page.classList.contains('is-empty')) {
          page.style.setProperty('box-shadow', this.bookSurfaceShadow, 'important');
        }
      });
    } else if (position === 'open') {
      this.book?.style.setProperty('border-radius', this.bookOuterRadius, 'important');
      this.book?.style.setProperty('box-shadow', this.bookSurfaceShadow, 'important');
    } else if (position === 'front') {
      this.rightPage?.style.setProperty('box-shadow', this.bookSurfaceShadow, 'important');
    } else if (position === 'back') {
      this.leftPage?.style.setProperty('box-shadow', this.bookSurfaceShadow, 'important');
    }
  }

  openSourceImage(trigger) {
    if (!trigger || !this.sourceDialog || !this.sourceDialogImage) return;

    const sourceUrl = trigger.dataset.hbnSourceUrl;
    if (!sourceUrl) return;

    this.lastSourceTrigger = trigger;
    this.sourceDialogImage.src = sourceUrl;
    this.sourceDialogImage.alt = trigger.dataset.hbnSourceAlt || '';

    if (this.sourceDialogCaption) {
      this.sourceDialogCaption.textContent = trigger.dataset.hbnSourceLabel || '';
    }

    if (typeof this.sourceDialog.showModal === 'function') {
      if (!this.sourceDialog.open) this.sourceDialog.showModal();
      return;
    }

    this.sourceDialog.setAttribute('open', '');
  }

  closeSourceImage() {
    if (!this.sourceDialog?.open) return;

    if (typeof this.sourceDialog.close === 'function') {
      this.sourceDialog.close();
      return;
    }

    this.sourceDialog.removeAttribute('open');
    this.resetSourceDialog();
  }

  resetSourceDialog() {
    this.sourceDialogImage?.removeAttribute('src');
    if (this.sourceDialogImage) this.sourceDialogImage.alt = '';
    if (this.sourceDialogCaption) this.sourceDialogCaption.textContent = '';

    const trigger = this.lastSourceTrigger;
    this.lastSourceTrigger = null;
    trigger?.focus?.({ preventScroll: true });
  }

  openBookPreview() {
    if (
      this.isAnimating ||
      this.isBookExpanded ||
      !this.viewer ||
      !this.bookDialog ||
      !this.bookDialogStage
    ) {
      return;
    }

    this.viewerAnchor = document.createComment('HBN story glimpse viewer');
    this.viewer.before(this.viewerAnchor);
    this.isBookExpanded = true;
    this.viewer.classList.add('hbn-story-glimpse__viewer--expanded');
    this.renderSpread(this.currentSpread);
    this.bookDialogStage.append(this.viewer);

    try {
      if (typeof this.bookDialog.showModal === 'function') {
        this.bookDialog.showModal();
      } else {
        this.bookDialog.setAttribute('open', '');
      }

      window.requestAnimationFrame(() => this.book?.focus?.({ preventScroll: true }));
    } catch (_error) {
      this.restoreBookViewer(false);
    }
  }

  closeBookPreview() {
    if (!this.bookDialog?.open) return;

    if (typeof this.bookDialog.close === 'function') {
      this.bookDialog.close();
      return;
    }

    this.bookDialog.removeAttribute('open');
    this.restoreBookViewer();
  }

  restoreBookViewer(restoreFocus = true) {
    if (!this.isBookExpanded || !this.viewer) return;

    if (this.viewerAnchor?.parentNode) {
      this.viewerAnchor.parentNode.insertBefore(this.viewer, this.viewerAnchor);
      this.viewerAnchor.remove();
    }

    this.viewerAnchor = null;
    this.viewer.classList.remove('hbn-story-glimpse__viewer--expanded');
    this.isBookExpanded = false;
    this.renderSpread(this.currentSpread);
    this.updateControls();

    if (restoreFocus) this.bookExpandButton?.focus?.({ preventScroll: true });
  }

  clonePage(pageIndex) {
    const template = this.pageTemplates[pageIndex];
    const image = template?.content?.querySelector('img');

    if (!image) return document.createTextNode('');

    const clone = image.cloneNode(true);
    clone.style.setProperty('border-radius', 'inherit', 'important');
    clone.style.setProperty('clip-path', 'none', 'important');
    clone.style.setProperty('-webkit-mask', 'none', 'important');
    clone.style.setProperty('mask', 'none', 'important');
    if (this.isBookExpanded) clone.setAttribute('sizes', '44vw');
    return clone;
  }

  setPage(container, pageIndex) {
    if (!container) return;

    const template = this.pageTemplates[pageIndex];
    const hasImage = Boolean(template?.content?.querySelector('img'));
    const pageKind = template?.dataset?.hbnPageKind || 'page';
    const isEmpty = !hasImage || pageKind === 'empty';

    container.classList.toggle('is-empty', isEmpty);
    container.classList.toggle('is-cover', hasImage && pageKind === 'cover');
    container.style.setProperty('visibility', isEmpty ? 'hidden' : 'visible', 'important');
    container.style.setProperty('background', 'transparent', 'important');

    if (isEmpty) {
      container.style.setProperty('box-shadow', 'none', 'important');
    }

    container.replaceChildren(this.clonePage(pageIndex));
  }

  renderSpread(spreadIndex) {
    if (this.coverMode && spreadIndex === 0) {
      this.book.dataset.hbnPosition = 'front';
    } else if (this.coverMode && spreadIndex === this.spreadCount - 1) {
      this.book.dataset.hbnPosition = 'back';
    } else {
      this.book.dataset.hbnPosition = 'open';
    }

    this.setPage(this.leftPage, spreadIndex * 2);
    this.setPage(this.rightPage, spreadIndex * 2 + 1);
    this.applyBookChrome();
    this.currentSpread = spreadIndex;
  }

  async goTo(targetSpread) {
    if (
      this.isAnimating ||
      targetSpread < 0 ||
      targetSpread >= this.spreadCount ||
      targetSpread === this.currentSpread
    ) {
      return;
    }

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      this.renderSpread(targetSpread);
      this.updateControls(true);
      return;
    }

    this.isAnimating = true;
    this.updateControls();

    const movingForward = targetSpread > this.currentSpread;
    const touchesCover = this.coverMode && [this.currentSpread, targetSpread].some(
      (spread) => spread === 0 || spread === this.spreadCount - 1
    );
    this.book.dataset.hbnPosition = 'open';

    this.leaf.className = `hbn-story-glimpse__leaf ${
      movingForward
        ? 'hbn-story-glimpse__leaf--forward'
        : 'hbn-story-glimpse__leaf--backward'
    }`;
    this.applyLeafGeometry(movingForward);

    if (movingForward) {
      this.setPage(this.rightPage, targetSpread * 2 + 1);
      this.setPage(this.leafFront, this.currentSpread * 2 + 1);
      this.setPage(this.leafBack, targetSpread * 2);
    } else {
      this.setPage(this.leftPage, targetSpread * 2);
      this.setPage(this.leafFront, this.currentSpread * 2);
      this.setPage(this.leafBack, targetSpread * 2 + 1);
    }

    this.applyBookChrome(touchesCover ? 'turning-cover' : 'open');
    this.leaf.hidden = false;
    this.leaf.getBoundingClientRect();

    await new Promise((resolve) => {
      let finished = false;

      const finish = (event) => {
        if (event && event.target !== this.leaf) return;
        if (event?.type === 'transitionend' && event.propertyName !== 'transform') return;
        if (finished) return;

        finished = true;
        this.leaf.removeEventListener('transitionend', finish);
        this.leaf.removeEventListener('transitioncancel', finish);
        window.clearTimeout(this.transitionFallback);
        resolve();
      };

      this.leaf.addEventListener('transitionend', finish);
      this.leaf.addEventListener('transitioncancel', finish);
      this.transitionFallback = window.setTimeout(finish, 1000);

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.leaf.classList.add('is-turned');
        });
      });
    });

    this.renderSpread(targetSpread);
    this.leaf.hidden = true;
    this.leaf.className = 'hbn-story-glimpse__leaf';
    this.leafFront.replaceChildren();
    this.leafBack.replaceChildren();
    this.isAnimating = false;
    this.updateControls(true);
  }

  updateControls(announce = false) {
    if (this.previousButton) {
      this.previousButton.disabled = this.isAnimating || this.currentSpread === 0;
    }

    if (this.nextButton) {
      this.nextButton.disabled =
        this.isAnimating || this.currentSpread === this.spreadCount - 1;
    }

    if (this.bookExpandButton) {
      this.bookExpandButton.disabled = this.isAnimating;
    }

    if (this.currentCounter) {
      this.currentCounter.textContent = String(this.currentSpread + 1);
    }

    if (announce && this.status) {
      const template = this.dataset.statusTemplate || '__CURRENT__ / __TOTAL__';
      this.status.textContent = template
        .replace('__CURRENT__', String(this.currentSpread + 1))
        .replace('__TOTAL__', String(this.spreadCount));
    }
  }

  handleBookKeydown(event) {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.goTo(this.currentSpread - 1);
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.goTo(this.currentSpread + 1);
    }
  }

  handlePointerDown(event) {
    if (event.pointerType === 'mouse' && event.button !== 0) return;

    this.pointerStart = {
      x: event.clientX,
      y: event.clientY,
      id: event.pointerId
    };
  }

  handlePointerUp(event) {
    if (!this.pointerStart || this.pointerStart.id !== event.pointerId) return;

    const deltaX = event.clientX - this.pointerStart.x;
    const deltaY = event.clientY - this.pointerStart.y;
    this.clearPointerStart();

    if (Math.abs(deltaX) < 44 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.15) return;

    if (deltaX < 0) {
      this.goTo(this.currentSpread + 1);
    } else {
      this.goTo(this.currentSpread - 1);
    }
  }

  clearPointerStart() {
    this.pointerStart = null;
  }

  preparePreloading() {
    if (this.pageTemplates.length <= 2) return;

    const preload = () => {
      this.pageTemplates.slice(2).forEach((template) => {
        const source = template.content.querySelector('img');
        if (!source) return;

        const image = new Image();
        const sizes = source.getAttribute('sizes');
        const srcset = source.getAttribute('srcset');
        const src = source.getAttribute('src');

        if (sizes) image.sizes = sizes;
        if (srcset) image.srcset = srcset;
        if (src) image.src = src;

        this.preloadedImages.push(image);
      });
    };

    if (!('IntersectionObserver' in window)) {
      preload();
      return;
    }

    this.preloadObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        preload();
      },
      { rootMargin: '300px 0px' }
    );

    this.preloadObserver.observe(this);
  }
}

if (!customElements.get('hbn-story-glimpse')) {
  customElements.define('hbn-story-glimpse', HbnStoryGlimpse);
}
